import { message, Spin, Table } from "antd"
import { memo, useEffect, useState } from "react"
import { useActions } from "../../../hooks/useActions"
import { useTypedSelector } from "../../../hooks/useTypedSelector"
import { TABLE_SCROLL_X } from "../constants/columns.constants"
import {
  buildBidAdjustmentItem,
  buildPlacementRowsForTable,
  computeBaseBid,
  computeCurrentBid,
  hasAudienceChange,
  hasModifierChanges,
  resolveAudienceId,
} from "../hooks/helpers/sbPlacementModifier.helper"
import { useSbPlacementsColumns } from "../hooks/useSbPlacementsColumns"
import { ISbPlacementRowForTable } from "../types/sbPlacement.types"
import styles from "./SponsoredBrandsPlacementsTable.module.scss"

interface ISponsoredBrandsPlacementsTable {
  campaignId: string
}

const SponsoredBrandsPlacementsTableComponent: React.FC<ISponsoredBrandsPlacementsTable> = ({
  campaignId,
}) => {
  const { placementData, loading, error } = useTypedSelector(
    (select) => select.sponsoredBrandsPlacements
  )
  const { targetDataForTable } = useTypedSelector((select) => select.sponsoredBrandsTargetForTable)
  const pendingAudienceId = useTypedSelector(
    (select) =>
      select.sponsoredBrandsPlacements.pendingAdjustments[campaignId]?.audiences?.[0]?.audienceId
  )
  const { setSbPlacementAdjustment } = useActions()

  const [rows, setRows] = useState<ISbPlacementRowForTable[]>([])
  const [audienceId, setAudienceId] = useState<string | null>(null)

  const campaignPlacements = placementData.find(
    (data) => String(data.campaignId) === String(campaignId)
  )
  const campaignTargets =
    targetDataForTable.find((data) => String(data.campaignId) === String(campaignId))?.targetData ??
    []
  const baseBid = computeBaseBid(campaignTargets) ?? campaignPlacements?.baseBid ?? null
  const currentBid = campaignPlacements?.currentBid ?? computeCurrentBid(campaignTargets)

  function recompute(nextRows: ISbPlacementRowForTable[]): ISbPlacementRowForTable[] {
    if (!campaignPlacements) {
      return nextRows
    }

    return buildPlacementRowsForTable(nextRows, campaignPlacements.bidding, baseBid, currentBid)
  }

  useEffect(() => {
    if (!campaignPlacements) {
      return
    }

    setRows(
      buildPlacementRowsForTable(
        campaignPlacements.placements,
        campaignPlacements.bidding,
        baseBid,
        currentBid
      )
    )
    setAudienceId(resolveAudienceId(campaignPlacements.bidding))
  }, [campaignPlacements, baseBid, currentBid])

  useEffect(() => {
    if (!pendingAudienceId) {
      return
    }

    setAudienceId((prev) => (prev === pendingAudienceId ? prev : pendingAudienceId))
  }, [pendingAudienceId])

  useEffect(() => {
    if (!campaignPlacements) {
      return
    }

    const { bidding } = campaignPlacements
    const audienceChanged = hasAudienceChange(bidding, audienceId)
    const changed = hasModifierChanges(rows, bidding) || audienceChanged

    setSbPlacementAdjustment(
      campaignId,
      changed
        ? buildBidAdjustmentItem(campaignId, rows, bidding, audienceChanged ? audienceId : null)
        : null
    )
  }, [rows, audienceId])

  useEffect(() => {
    if (error) {
      message.error("Failed to load placements")
    }
  }, [error])

  function changeModifier(placement: string, value: number) {
    setRows((prev) =>
      prev.map((row) => (row.placement === placement ? { ...row, modifier: value } : row))
    )
  }

  function changeTargetAcos(placement: string, value: number) {
    setRows((prev) =>
      recompute(
        prev.map((row) => (row.placement === placement ? { ...row, targetAcos: value } : row))
      )
    )
  }

  function changeAudienceId(value: string) {
    setAudienceId(value)
    setRows((prev) => recompute(prev))
  }

  const columns = useSbPlacementsColumns(
    changeModifier,
    changeTargetAcos,
    changeAudienceId,
    audienceId
  )

  if (!campaignPlacements) {
    return <Spin />
  }

  return (
    <div className={styles.placementsBlock}>
      <Table
        columns={columns}
        dataSource={rows}
        rowKey="placement"
        pagination={false}
        loading={loading}
        tableLayout="fixed"
        scroll={{ x: TABLE_SCROLL_X }}
      />
    </div>
  )
}

export const SponsoredBrandsPlacementsTable = memo(SponsoredBrandsPlacementsTableComponent)
